/**
 * DyeingProductionEntry · P17-UAT-18A-3 · Dyeing Material Transaction Contract.
 *
 * Register-first Dyeing screen + "+ Record Dyeing" dialog on the SAME screen (no
 * separate menu). WO-first authority (18A-2 preserved): WO primary · SO/ESO + Product
 * read-only. Source = DYE-1FLR (read-only), exact eligible Source Batch. KG is the
 * physical authority; Metres are system-derived (read-only) from the approved BOM
 * Standard g/m. Loss KG = Input − Output (operator never enters Loss). Output → PACK-WIP
 * (SFG). All stock-posting rules live in the backend dyeing_run engine — this screen
 * only captures + displays.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import StatusBadge from "@/components/StatusBadge";
import {
  TransactionSection, FormField, NumericField, ReferenceSelect, ReadOnlyField,
  txnTextareaCls, txnBtn, txnBtnSize,
} from "@/components/txn";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { PlusIcon, FloppyDiskIcon, CheckCircleIcon, XIcon, ArrowCounterClockwiseIcon } from "@phosphor-icons/react";

const TID = "dyeing-entry";
const num = (v) => (v === "" || v === null || v === undefined ? 0 : Number(v) || 0);
const fmt = (v, d = 3) =>
  v === null || v === undefined || v === "" || isNaN(Number(v))
    ? "—"
    : Number(v).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
const SHIFTS = ["Day", "Night", "General"];
const today = () => new Date().toISOString().slice(0, 10);

export default function DyeingProductionEntry() {
  const { hasPerm } = useAuth();
  const canEdit = hasPerm ? hasPerm("inventory:edit") : true;

  const [rows, setRows] = useState([]);
  const [wos, setWos] = useState([]);
  const [machines, setMachines] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  // form state
  const [woId, setWoId] = useState("");
  const [itemId, setItemId] = useState("");
  const [machineId, setMachineId] = useState("");
  const [shift, setShift] = useState("Day");
  const [entryDate, setEntryDate] = useState(today());
  const [sources, setSources] = useState([]);
  const [srcBatch, setSrcBatch] = useState("");
  const [gpm, setGpm] = useState(null);
  const [inputKg, setInputKg] = useState("");
  const [outputKg, setOutputKg] = useState("");
  const [lossReason, setLossReason] = useState("");
  const [remarks, setRemarks] = useState("");
  const [draftId, setDraftId] = useState(null);
  const [draftNo, setDraftNo] = useState(null);
  const [pv, setPv] = useState(null);

  const loadRows = () => api.get("/dyeing-production-entries").then(r => setRows(r.data || [])).catch(() => setRows([]));
  useEffect(() => {
    loadRows();
    api.get("/work-orders/").then(r => setWos(r.data || [])).catch(() => setWos([]));
    api.get("/machines/").then(r => setMachines((r.data || []).filter(m => (m.department || "").toLowerCase() === "dyeing")))
      .catch(() => setMachines([]));
  }, []);

  const wo = useMemo(() => wos.find(w => w.id === woId), [wos, woId]);
  const items = useMemo(() => {
    if (!wo) return [];
    const li = wo.items && wo.items.length ? wo.items : [{ item_id: wo.item_id, product_code: wo.item_code || wo.product_code }];
    return li.filter(x => x.item_id);
  }, [wo]);
  const selItem = useMemo(() => items.find(x => x.item_id === itemId), [items, itemId]);
  const product = selItem ? (selItem.product_code || selItem.item_code) : "";
  const soEso = wo ? (wo.source_order_no || "") : "";
  const selSource = useMemo(() => sources.find(s => s.batch_lot === srcBatch), [sources, srcBatch]);
  const availKg = selSource ? Number(selSource.available_kg) : 0;

  useEffect(() => { if (items.length === 1) setItemId(items[0].item_id); }, [items]);

  useEffect(() => {
    setSources([]); setSrcBatch("");
    if (!woId || !product) return;
    api.get(`/dyeing/eligible-sources?wo_id=${encodeURIComponent(woId)}&product_code=${encodeURIComponent(product)}`)
      .then(r => {
        const s = (r.data && r.data.sources) || [];
        setSources(s);
        setGpm(r.data && r.data.bom_standard_gpm != null ? Number(r.data.bom_standard_gpm) : null);
        if (s.length === 1) setSrcBatch(s[0].batch_lot);
      })
      .catch(() => setSources([]));
  }, [woId, product]);

  const inN = num(inputKg), outN = num(outputKg);
  const lossN = inN > 0 && outN > 0 ? Math.round((inN - outN) * 1000) / 1000 : 0;
  const mOf = (kg) => (gpm && gpm > 0 ? Math.round((Number(kg || 0) * 1000 / gpm) * 1000) / 1000 : null);
  const fmM = (kg) => { const m = mOf(kg); return m == null ? "—" : fmt(m, 3); };
  const conserved = inN > 0 && outN > 0 && outN <= inN + 1e-6;
  const stockOk = inN > 0 && inN <= availKg + 1e-6;
  const canSave = canEdit && conserved && stockOk && !!srcBatch && !!machineId;

  const resetForm = () => {
    setWoId(""); setItemId(""); setMachineId(""); setShift("Day"); setEntryDate(today()); setSources([]); setSrcBatch("");
    setGpm(null); setInputKg(""); setOutputKg(""); setLossReason(""); setRemarks("");
    setDraftId(null); setDraftNo(null); setPv(null);
  };
  const openNew = () => { resetForm(); setOpen(true); };

  const woOptions = useMemo(() => wos
    .filter(w => !["cancelled", "rejected", "closed"].includes((w.status || "").toLowerCase()))
    .map(w => {
      const li = (w.items && w.items[0]) || {};
      return { value: w.id, code: w.po_no || w.wo_no || "", name: li.product_code || li.item_code || w.item_code || w.product_code || "", meta: w.source_order_no || "" };
    }), [wos]);
  const itemOptions = useMemo(() => items.map(it => ({ value: it.item_id, code: it.product_code || it.item_code || "", name: it.style_no || "" })), [items]);
  const machineOptions = useMemo(() => machines.map(m => ({ value: m.id, code: m.code || m.name || "", name: "Dyeing" })), [machines]);
  const sourceOptions = useMemo(() => sources.map(s => ({ value: s.batch_lot, code: s.batch_lot, meta: `${fmt(s.available_kg, 3)} kg` })), [sources]);

  const saveDraft = async () => {
    if (!woId || !itemId) return toast.error("Select a Work Order");
    if (!machineId) return toast.error("Select a Dyeing machine");
    if (!srcBatch) return toast.error("Select a DYE-1FLR source batch");
    setBusy(true);
    try {
      const body = {
        work_order_id: woId, item_id: itemId, product_code: product, machine_id: machineId,
        shift, source_batch_lot: srcBatch, input_kg: inN, output_kg: outN,
        loss_reason: lossReason, remarks, entry_date: entryDate,
      };
      let id = draftId;
      if (!id) {
        const r = await api.post("/dyeing-production-entries", body);
        id = r.data.id; setDraftId(id); setDraftNo(r.data.entry_no);
      } else {
        await api.put(`/dyeing-production-entries/${id}`, body);
      }
      const p = await api.get(`/production-entries/${id}/dyeing-preview`).then(r => r.data)
        .catch(e => ({ ok: false, reasons: [e?.response?.data?.detail || "preview error"] }));
      setPv(p);
      toast.success(`Draft saved · ${draftNo || r_no(p) || "DPE"}`);
      loadRows();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };
  const r_no = (p) => (p && p.header && p.header.entry_no) || "";

  const postDyeing = async () => {
    if (!draftId) return toast.error("Save the draft first");
    setBusy(true);
    try {
      const r = await api.post(`/dyeing-production-entries/${draftId}/dyeing-post`, {});
      toast.success(`Dyeing posted · ${r.data?.output_kg} kg → ${r.data?.destination_godown} · ${r.data?.output_batch}`);
      setOpen(false); resetForm(); loadRows();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Post failed");
    } finally { setBusy(false); }
  };

  const reverseRow = async (row) => {
    const reason = window.prompt(`Reverse ${row.entry_no}? Enter a reason (min 4 chars):`);
    if (!reason || reason.trim().length < 4) return;
    try {
      await api.post(`/dyeing-production-entries/${row.id}/dyeing-reverse`, { reason: reason.trim() });
      toast.success(`${row.entry_no} reversed`); loadRows();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reverse failed"); }
  };

  const postable = pv && pv.mode === "PLAN" && pv.postable;

  return (
    <div data-testid={`${TID}-page`} className="space-y-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="overline mb-1">Dyeing Production · Register</div>
          <h2 className="font-display text-xl font-black tracking-tight text-white">Dyeing Production</h2>
        </div>
        <button onClick={openNew} disabled={!canEdit} data-testid={`${TID}-record-btn`}
          className={`${txnBtn.primary} ${txnBtnSize.standard}`}>
          <PlusIcon size={15} weight="bold" /> Record Dyeing
        </button>
      </div>

      {/* Register */}
      <div className="overflow-x-auto rounded-sm border border-[var(--border)]" data-testid={`${TID}-register`}>
        <table className="w-full text-xs">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-[var(--fg-3)] text-left border-b border-[var(--border)] bg-[var(--bg-2)]">
              {["DPE No", "Date", "WO", "SO/ESO", "Product", "Machine", "Source Batch",
                "Input KG", "Input M", "Output KG", "Output M", "Loss KG", "Loss M",
                "Output Batch", "Status", "V2", "Action"].map(h => (
                <th key={h} className="py-2 px-2 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={17} className="py-6 text-center text-[var(--fg-3)]" data-testid={`${TID}-empty`}>No Dyeing entries yet. Click “Record Dyeing”.</td></tr>
            ) : rows.map(r => (
              <tr key={r.id} className="border-b border-[var(--border)]/60" data-testid={`${TID}-row-${r.entry_no}`}>
                <td className="py-1.5 px-2 font-mono whitespace-nowrap">{r.entry_no}</td>
                <td className="py-1.5 px-2 whitespace-nowrap">{r.entry_date}</td>
                <td className="py-1.5 px-2 font-mono whitespace-nowrap">{r.wo_no || "—"}</td>
                <td className="py-1.5 px-2 font-mono whitespace-nowrap">{r.so_eso || "—"}</td>
                <td className="py-1.5 px-2 font-mono whitespace-nowrap">{r.product_code || "—"}</td>
                <td className="py-1.5 px-2 whitespace-nowrap">{r.machine || "—"}</td>
                <td className="py-1.5 px-2 font-mono whitespace-nowrap">{r.source_batch || "—"}</td>
                <td className="py-1.5 px-2 font-mono text-right">{fmt(r.input_kg)}</td>
                <td className="py-1.5 px-2 font-mono text-right text-[var(--fg-3)]">{fmt(r.input_m)}</td>
                <td className="py-1.5 px-2 font-mono text-right">{fmt(r.output_kg)}</td>
                <td className="py-1.5 px-2 font-mono text-right text-[var(--fg-3)]">{fmt(r.output_m)}</td>
                <td className="py-1.5 px-2 font-mono text-right text-rose-400">{fmt(r.loss_kg)}</td>
                <td className="py-1.5 px-2 font-mono text-right text-[var(--fg-3)]">{fmt(r.loss_m)}</td>
                <td className="py-1.5 px-2 font-mono whitespace-nowrap">{r.output_batch || "—"}</td>
                <td className="py-1.5 px-2"><StatusBadge status={r.status === "POSTED" ? "Authorised" : r.status === "REVERSED" ? "Reversed" : "Draft"} /></td>
                <td className="py-1.5 px-2 whitespace-nowrap text-[10px] text-[var(--fg-3)]">{r.v2_posting_status}</td>
                <td className="py-1.5 px-2">
                  {r.status === "POSTED" && canEdit && (
                    <button onClick={() => reverseRow(r)} data-testid={`${TID}-reverse-${r.entry_no}`}
                      className="text-[var(--fg-3)] hover:text-rose-400 transition inline-flex items-center gap-1">
                      <ArrowCounterClockwiseIcon size={13} weight="bold" /> Reverse
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Record Dyeing dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid={`${TID}-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              New Dyeing Entry {draftNo && <span className="font-mono text-sm text-[var(--fg-2)]">· {draftNo}</span>}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <TransactionSection index={1} title="Document & Machine" testId={`${TID}-sec-doc`}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField label="Date" required testId={`${TID}-date-field`}>
                  <input type="date" value={entryDate} max={today()} onChange={e => setEntryDate(e.target.value || today())}
                    data-testid={`${TID}-date`}
                    className="h-9 w-full px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm text-[var(--fg-0)] font-mono focus:outline-none focus:border-[var(--accent)]" />
                </FormField>
                <FormField label="Shift" testId={`${TID}-shift-field`}>
                  <select value={shift} onChange={e => setShift(e.target.value)} data-testid={`${TID}-shift`}
                    className="h-9 w-full px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm text-[var(--fg-0)] focus:outline-none focus:border-[var(--accent)]">
                    {SHIFTS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </FormField>
                <FormField label="Machine" required testId={`${TID}-machine-field`}>
                  <ReferenceSelect testid={`${TID}-machine`} options={machineOptions} value={machineId} onChange={setMachineId}
                    placeholder={machineOptions.length ? "— Select Dyeing machine —" : "No Dyeing machine configured"} />
                </FormField>
                <FormField label="Operator" testId={`${TID}-operator-field`}>
                  <ReadOnlyField testId={`${TID}-operator`} value="—" tone="muted" />
                </FormField>
                <FormField label="Work Order" required testId={`${TID}-wo-field`}>
                  <ReferenceSelect testid={`${TID}-wo`} options={woOptions} value={woId}
                    onChange={(v) => { setWoId(v); setItemId(""); setDraftId(null); setDraftNo(null); setPv(null); }}
                    placeholder="— Select WO · Product · SO/ESO —" emptyLabel="No eligible work orders." />
                </FormField>
                <FormField label="SO / ESO Reference" testId={`${TID}-soeso-field`}>
                  <ReadOnlyField testId={`${TID}-soeso`} value={soEso} mono tone="muted" />
                </FormField>
                {items.length > 1 && (
                  <FormField label="WO Item" required testId={`${TID}-item-field`}>
                    <ReferenceSelect testid={`${TID}-item`} options={itemOptions} value={itemId} onChange={setItemId} placeholder="— Select Item —" />
                  </FormField>
                )}
                <div className="sm:col-span-2">
                  <FormField label="Item / Product" testId={`${TID}-product-field`}>
                    <div data-testid={`${TID}-product`} title={product || "—"}
                      className={`min-h-9 px-3 py-1.5 flex items-center bg-[var(--bg-2)] border border-dashed border-[var(--border)] rounded-sm text-sm font-mono break-all leading-snug ${product ? "text-[var(--accent)]" : "text-[var(--fg-3)]"}`}>
                      {product || "—"}
                    </div>
                  </FormField>
                </div>
              </div>
            </TransactionSection>

            <TransactionSection index={2} title="Source (DYE-1FLR)" testId={`${TID}-sec-source`}>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <FormField label="Source Godown" testId={`${TID}-godown-field`}>
                  <ReadOnlyField testId={`${TID}-godown`} value={selSource ? (selSource.godown_code || "DYE-1FLR") : "DYE-1FLR"} mono tone="muted" />
                </FormField>
                <FormField label="Source Batch" required testId={`${TID}-source-field`}>
                  <ReferenceSelect testid={`${TID}-source`} options={sourceOptions} value={srcBatch} onChange={setSrcBatch}
                    placeholder={sources.length ? "— Select batch —" : "No eligible DYE-1FLR stock"}
                    emptyLabel="No eligible Dyeing stock at DYE-1FLR." disabled={!sources.length} />
                </FormField>
                <FormField label="BOM Standard" testId={`${TID}-gpm-field`}>
                  <ReadOnlyField testId={`${TID}-gpm`} value={gpm ? `${fmt(gpm, 1)} g/m` : "—"} mono tone="sky" />
                </FormField>
                <FormField label="Available (kg)" testId={`${TID}-avail-field`}>
                  <ReadOnlyField testId={`${TID}-avail`} value={fmt(availKg)} mono tone="emerald" className="justify-end" />
                </FormField>
                <FormField label="Available (m)" testId={`${TID}-avail-m-field`}>
                  <ReadOnlyField testId={`${TID}-avail-m`} value={fmM(availKg)} mono tone="emerald" className="justify-end" />
                </FormField>
                <FormField label="Destination" testId={`${TID}-dest-field`}>
                  <ReadOnlyField testId={`${TID}-dest`} value="PACK-WIP" mono tone="muted" />
                </FormField>
              </div>
            </TransactionSection>

            <TransactionSection index={3} title="Dyeing Quantities (KG primary · Metres derived)" testId={`${TID}-sec-qty`}>
              <div className="space-y-2" data-testid={`${TID}-qty-grid`}>
                <div className="grid grid-cols-[1.3fr_1fr_1fr] gap-3 items-center px-0.5 overline text-[var(--fg-3)]">
                  <span>Quantity</span><span className="text-right">KG</span><span className="text-right">Metres</span>
                </div>
                <div className="grid grid-cols-[1.3fr_1fr_1fr] gap-3 items-center" data-testid={`${TID}-row-input`}>
                  <label className="text-sm text-[var(--fg-1)]">Input <span className="text-red-400">*</span></label>
                  <NumericField data-testid={`${TID}-input-kg`} value={inputKg} onChange={e => setInputKg(e.target.value)} invalid={inN > 0 && !stockOk} />
                  <ReadOnlyField testId={`${TID}-input-m`} value={fmM(inN)} mono tone="muted" className="justify-end" />
                </div>
                <div className="grid grid-cols-[1.3fr_1fr_1fr] gap-3 items-center" data-testid={`${TID}-row-output`}>
                  <label className="text-sm text-[var(--fg-1)]">Output <span className="text-red-400">*</span></label>
                  <NumericField data-testid={`${TID}-output-kg`} value={outputKg} onChange={e => setOutputKg(e.target.value)} invalid={inN > 0 && outN > 0 && !conserved} />
                  <ReadOnlyField testId={`${TID}-output-m`} value={fmM(outN)} mono tone="muted" className="justify-end" />
                </div>
                <div className="grid grid-cols-[1.3fr_1fr_1fr] gap-3 items-center" data-testid={`${TID}-row-loss`}>
                  <label className="text-sm text-[var(--fg-1)]">Process Loss / Wastage</label>
                  <ReadOnlyField testId={`${TID}-loss-kg`} value={fmt(lossN)} mono tone="rose" className="justify-end" />
                  <ReadOnlyField testId={`${TID}-loss-m`} value={fmM(lossN)} mono tone="rose" className="justify-end" />
                </div>
              </div>
              <div data-testid={`${TID}-conservation`}
                className={`mt-3 flex items-center gap-2 rounded-sm px-3 py-2 text-xs font-mono border ${
                  inN <= 0 || outN <= 0 ? "border-[var(--border)] text-[var(--fg-3)]"
                    : conserved && stockOk ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/40"
                    : "bg-red-500/10 text-red-400 border-red-500/50"}`}>
                <span>{fmt(inN)} = {fmt(outN)} + {fmt(lossN)}</span>
                <span className="ml-auto font-sans font-semibold tracking-wide">
                  {inN <= 0 || outN <= 0 ? "ENTER INPUT & OUTPUT"
                    : !conserved ? "✕ OUTPUT > INPUT"
                    : !stockOk ? `✕ EXCEEDS AVAILABLE ${fmt(availKg)} KG`
                    : "✓ BALANCED"}
                </span>
              </div>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField label="Loss Reason (optional)" testId={`${TID}-lossreason-field`}>
                  <input value={lossReason} onChange={e => setLossReason(e.target.value)} data-testid={`${TID}-lossreason`}
                    className="h-9 w-full px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm text-[var(--fg-0)] focus:outline-none focus:border-[var(--accent)]"
                    placeholder="e.g. shrinkage / shade rejection" />
                </FormField>
              </div>
            </TransactionSection>

            <TransactionSection index={4} title="Remarks" testId={`${TID}-sec-remarks`}>
              <textarea data-testid={`${TID}-remarks`} value={remarks} onChange={e => setRemarks(e.target.value)}
                className={txnTextareaCls} placeholder="Optional notes for this Dyeing entry" />
            </TransactionSection>

            {pv && pv.mode === "PLAN" && !pv.postable && pv.reasons && pv.reasons.length > 0 && (
              <div className="rounded-sm bg-amber-500/10 border border-amber-500/40 px-3 py-2 text-[11px] text-amber-400" data-testid={`${TID}-preview-reasons`}>
                {pv.reasons.join(" · ")}
              </div>
            )}
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <button onClick={() => { setOpen(false); resetForm(); }} data-testid={`${TID}-cancel`} className={`${txnBtn.ghost} ${txnBtnSize.standard}`}>
              <XIcon size={14} weight="bold" /> Cancel
            </button>
            <button onClick={saveDraft} disabled={!canSave || busy} data-testid={`${TID}-save`} className={`${txnBtn.secondary} ${txnBtnSize.standard}`}>
              <FloppyDiskIcon size={15} weight="bold" /> {draftId ? "Update Draft" : "Save Draft"}
            </button>
            <button onClick={postDyeing} disabled={busy || !draftId || !postable} data-testid={`${TID}-post`}
              title={!draftId ? "Save the draft first" : (pv && !pv.postable ? "Preconditions not met" : "")}
              className={`${txnBtn.primary} ${txnBtnSize.wide}`}>
              <CheckCircleIcon size={15} weight="bold" /> Post Dyeing Entry
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
